/**
 * Client-side identity resolution (F6, ADR-12 "resolution = fold"). Accumulates
 * identity.* envelopes per did and re-folds with @beacon/crypto's foldIdentity —
 * the chain rules live in ONE audited place. foldIdentity aborts on a bad
 * genesis; since an adversary can race a fake first envelope, resolve() prunes
 * leading envelopes until a fold succeeds (a fake genesis cannot self-certify,
 * so pruning converges on the real one). Re-fold + prune is quadratic worst
 * case on adversarial input — bounded in the pilot (short chains, rotations
 * are rare events), carried as a note for A3 (the server-side projector owns
 * the scalable version).
 *
 * verdict() exposes the fold's per-tid outcome: subscription.ts uses the fold
 * as the verification authority for identity events, because a rotateKey is
 * legitimately signed by the PREVIOUS head — comparing its signingKey against
 * the post-ingest chain head would wrongly flag it stale.
 */
import { foldIdentity, type CborValue, type RejectReason } from "@beacon/crypto";

export const IDENTITY_TYPES: ReadonlySet<string> = new Set([
  "app.aid.identity.declareKeys",
  "app.aid.identity.rotateKey",
]);

export interface ResolvedKeys {
  signingKey: string;
  encryptionKey: string | null;
}

/** "accepted" / RejectReason per the last successful fold; "unresolved" when
 *  the did has no foldable chain yet or the tid was pruned as a fake genesis. */
export type FoldVerdict = "accepted" | RejectReason | "unresolved";

export class IdentityDirectory {
  private chains = new Map<string, Record<string, CborValue>[]>();
  private folds = new Map<string, ResolvedKeys>();
  private verdicts = new Map<string, Map<string, "accepted" | RejectReason>>();
  private dirty = new Set<string>();
  private seen = new Set<string>(); // "did\ntid" — dedupe across at-least-once redelivery

  /** Feed every received identity.* envelope (CBOR form). Cheap no-op for other types. */
  ingest(envelope: Record<string, CborValue>): void {
    const type = envelope.$type;
    const did = envelope.identityId;
    if (typeof type !== "string" || !IDENTITY_TYPES.has(type)) return;
    if (typeof did !== "string") return;
    // Lexicon requires `id` (tid) on every envelope, so an id-less envelope can
    // only reach here through a caller bypassing parseWire — skip it: it cannot
    // carry a verdict key and would only ever fold as a prune candidate.
    if (typeof envelope.id !== "string") return;
    const key = `${did}\n${envelope.id}`;
    if (this.seen.has(key)) return;
    this.seen.add(key);
    const chain = this.chains.get(did) ?? [];
    chain.push(envelope);
    this.chains.set(did, chain);
    this.dirty.add(did);
  }

  /** Current chain head for a did, or null while no valid genesis is known. */
  async resolve(did: string): Promise<ResolvedKeys | null> {
    if (!this.dirty.has(did)) return this.folds.get(did) ?? null;
    const chain = this.chains.get(did) ?? [];
    while (chain.length > 0) {
      try {
        const fold = await foldIdentity(did, chain);
        const verdict = new Map<string, "accepted" | RejectReason>();
        for (const tid of fold.accepted) verdict.set(tid, "accepted");
        for (const r of fold.rejected) verdict.set(r.tid, r.reason);
        const keys: ResolvedKeys = { signingKey: fold.signingKey, encryptionKey: fold.encryptionKey };
        this.folds.set(did, keys);
        this.verdicts.set(did, verdict);
        this.dirty.delete(did);
        return keys;
      } catch (err) {
        // Only genesis failures abort a fold, and chain.ts prefixes every one
        // with "identity fold:". Anything else (crypto runtime failure, bug) is
        // NOT evidence of a poison genesis — rethrow instead of silently
        // pruning a legitimate chain head.
        if (!(err instanceof Error && err.message.startsWith("identity fold:"))) throw err;
        chain.shift(); // bad genesis candidate — prune and retry
      }
    }
    // Fully pruned: drop all state for the did (a real genesis arriving later
    // starts a fresh chain instead of appending to a zombie entry).
    this.chains.delete(did);
    this.dirty.delete(did);
    this.folds.delete(did);
    this.verdicts.delete(did);
    return null;
  }

  /** Fold outcome for one ingested envelope (re-folds if the chain changed).
   *  Keyed by envelope id == wire id — parseWire enforces that equality, so a
   *  subscription can safely look up the verdict under the wire header's id. */
  async verdict(did: string, tid: string): Promise<FoldVerdict> {
    await this.resolve(did);
    return this.verdicts.get(did)?.get(tid) ?? "unresolved";
  }
}
